/**
 * UnmaskControl — drop-in control for the masked customer dashboard.
 *
 *   Masked   → a notice + "Reveal details" button that runs the OTP flow.
 *   Unmasked → a notice + live "hidden again in mm:ss" countdown badge.
 *
 * Built entirely from @finmin/ui primitives + semantic design tokens, so it
 * reads correctly in light and dark mode and on any page background. Reuses the
 * existing OtpChallengeDialog (features/profile) for the 6-digit verification.
 *
 * There is intentionally no manual "hide now": the unmask access token stays
 * valid server-side until it expires, so we cannot force-mask early without a
 * proactive refresh. The auto-revert timer in the context handles expiry.
 */
import * as React from 'react';
import { Button, Badge } from '@finmin/ui';
import { Eye, ShieldCheck, ShieldOff, Loader2 } from 'lucide-react';
import { OtpChallengeDialog } from '@/features/profile/components/otp-challenge-dialog';
import { useUnmask } from '../unmask-context';
import { useUnmaskChallenge } from '../use-unmask-challenge';

/** Live seconds remaining until `expiresAt` (epoch ms), or 0 when null/elapsed. */
function useCountdown(expiresAt: number | null): number {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (expiresAt === null) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  if (expiresAt === null) return 0;
  return Math.max(0, Math.ceil((expiresAt - now) / 1000));
}

export function UnmaskControl({
  className,
  compact = false,
}: {
  className?: string;
  /**
   * Compact form: just the "Reveal details" button (masked) or the countdown
   * badge (unmasked) — no surrounding notice box. For tight spots like a card
   * header. The default form keeps the full notice + control bar.
   */
  compact?: boolean;
}) {
  const { unmaskExpiresAt } = useUnmask();
  const {
    isOpen,
    isStarting,
    isVerifying,
    otpExpiresInSec,
    errorMessage,
    isDashboardUnmasked,
    start,
    verify,
    cancel,
  } = useUnmaskChallenge();

  const remaining = useCountdown(unmaskExpiresAt);
  const mm = Math.floor(remaining / 60);
  const ss = String(remaining % 60).padStart(2, '0');

  const dialog = (
    <OtpChallengeDialog
      open={isOpen}
      otpTarget={null}
      expiresInSec={otpExpiresInSec}
      isVerifying={isVerifying}
      errorMessage={errorMessage}
      onSubmit={verify}
      onCancel={cancel}
    />
  );

  if (compact) {
    return (
      <>
        {isDashboardUnmasked ? (
          <Badge
            className={[
              'gap-1.5 border-success/20 bg-success/10 text-success tabular-nums',
              className ?? '',
            ].join(' ')}
          >
            <ShieldOff className="h-3 w-3 shrink-0" aria-hidden />
            {mm}:{ss}
          </Badge>
        ) : (
          <Button
            size="sm"
            onClick={() => void start()}
            disabled={isStarting}
            className={['shrink-0 gap-1.5', className ?? ''].join(' ')}
          >
            {isStarting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Eye className="h-3.5 w-3.5" aria-hidden />
            )}
            Reveal details
          </Button>
        )}
        {dialog}
      </>
    );
  }

  return (
    <>
      <div
        className={[
          'flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3',
          isDashboardUnmasked ? 'border-success/30 bg-success/5' : 'border-border bg-background',
          className ?? '',
        ].join(' ')}
      >
        {isDashboardUnmasked ? (
          <>
            <div className="flex items-center gap-2 text-sm text-foreground">
              <ShieldOff className="h-4 w-4 shrink-0 text-success" aria-hidden />
              <span>Your sensitive details are visible.</span>
            </div>
            <Badge className="gap-1.5 border-success/20 bg-success/10 text-success tabular-nums">
              Hidden again in {mm}:{ss}
            </Badge>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <span>Details like PAN, date of birth and contact info are hidden for your security.</span>
            </div>
            <Button
              size="sm"
              onClick={() => void start()}
              disabled={isStarting}
              className="shrink-0 gap-1.5"
            >
              {isStarting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Eye className="h-3.5 w-3.5" aria-hidden />
              )}
              Reveal details
            </Button>
          </>
        )}
      </div>

      {dialog}
    </>
  );
}
